import { useState } from 'react';
import { Modal as QcsModal } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../common/basic/QcsButton';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { QcsTypography } from '../common/basic/QcsTypography';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { SaveError } from '../common/SaveError';
import { ErrorStateType } from '../../models/common';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';
import { setErrorStateSnacks } from '../../utils/error';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import {
  ButtonsRow,
  CardCheck,
  CardLabel,
  CardsGrid,
  ChoiceCard,
  ConflictHeader,
  FieldLabel,
  FieldRow,
  FieldValue,
} from './VisitationAddModalOneTimeConflict.styles';

export interface ConflictPersonForm {
  name: string;
  lastName: string;
  phone?: string;
}

export interface VisitationAddOneTimeConflictData<T extends ConflictPersonForm = ConflictPersonForm> {
  form: T;
  db: SupplierEmployeeDto;
}

interface VisitationAddOneTimeConflictForm {
  type: 'db' | 'form';
}

interface Props<T extends ConflictPersonForm> {
  data: VisitationAddOneTimeConflictData<T> | undefined;
  onClose: (form: T | undefined) => Promise<void> | void;
}

interface ConflictCardsProps {
  data: VisitationAddOneTimeConflictData;
}

const ConflictCards = ({ data }: ConflictCardsProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<VisitationAddOneTimeConflictForm>();

  const sides: { key: 'db' | 'form'; label: string; record: ConflictPersonForm }[] = [
    { key: 'db', label: t('visitation.oneTimeConflictUseDb'), record: data.db },
    { key: 'form', label: t('visitation.oneTimeConflictUseForm'), record: data.form },
  ];

  return (
    <CardsGrid>
      {sides.map(({ key, label, record }) => {
        const active = values.type === key;
        return (
          <ChoiceCard
            key={key}
            type="button"
            $active={active}
            aria-pressed={active}
            onClick={() => setFieldValue('type', key)}
          >
            <CardCheck $active={active} aria-hidden="true">
              <CheckCircleRoundedIcon fontSize="small" />
            </CardCheck>
            <CardLabel>{label}</CardLabel>
            <FieldRow>
              <FieldLabel>{t('visitation.oneTimeName')}</FieldLabel>
              <FieldValue>{record.name || '—'}</FieldValue>
            </FieldRow>
            <FieldRow>
              <FieldLabel>{t('visitation.oneTimeLastName')}</FieldLabel>
              <FieldValue>{record.lastName || '—'}</FieldValue>
            </FieldRow>
            <FieldRow>
              <FieldLabel>{t('visitation.oneTimePhone')}</FieldLabel>
              <FieldValue>{record.phone || '—'}</FieldValue>
            </FieldRow>
          </ChoiceCard>
        );
      })}
    </CardsGrid>
  );
};

export function VisitationAddModalOneTimeConflict<T extends ConflictPersonForm>({ data, onClose }: Props<T>) {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState<ErrorStateType>();
  const { enqueueErrorSnackbar } = useAppSnackbar();

  const handleSubmit = async (formData: VisitationAddOneTimeConflictForm) => {
    setSaveError('');
    if (!data) return;
    try {
      if (formData.type === 'db') {
        const merged: T = {
          ...data.form,
          name: data.db.name,
          lastName: data.db.lastName,
          phone: data.db.phone,
        };
        await onClose(merged);
      } else {
        await onClose(data.form);
      }
    } catch (err) {
      setErrorStateSnacks(err, setSaveError, enqueueErrorSnackbar, 'common.saveError');
    }
  };

  return (
    <QcsModal open={!!data} onClose={() => onClose(undefined)}>
      <QcsModalBox>
        <ConflictHeader>
          <QcsTypography variant="h5">{t('visitation.oneTimeConflictTitle')}</QcsTypography>
        </ConflictHeader>
        <FormikDebounce<VisitationAddOneTimeConflictForm> initialValues={{ type: 'db' }} onSubmit={handleSubmit}>
          <Form>
            <FormContainer>
              {data && <ConflictCards data={data} />}
              <SaveError error={saveError} />
              <ButtonsRow>
                <QcsButton variant="outlined" onClick={() => onClose(undefined)}>
                  {t('common.cancel')}
                </QcsButton>
                <SubmitButton text={t('common.confirm')} ignoreMarginLeft />
              </ButtonsRow>
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
}
